import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ReactiveDropdownSyncController } from '@fitogest/reactive-form-builder/utils';

import { DashboardService } from '../../../../../service/events/dashboard.service';
import { BaseForm } from '../../../../base-form';
import { EntidadesApiService } from '../../../../../service/api/entidades-api.service';
import { EntidadModel } from 'src/app/models/reto/entidad.model';
import { ProvinciasInterface } from 'src/app/models/reto/provincia.model';
import { FormType, ReactiveFormStableValueChange } from '@fitogest/reactive-form-builder/interfaces';
import {
    EntidadesDropdownOption,
    EntidadesFormDefinition,
} from './form-control/form-definition';

@Component({
    selector: 'app-entidades-form',
    standalone: false,
    templateUrl: './entidades-form.html',
    styleUrls: ['./entidades-form.scss'],
})
export class EntidadesFormComponent extends BaseForm<EntidadModel> implements OnInit {
    private static readonly SPAIN_COUNTRY_CODE = 'ES';

    public form!: EntidadesFormDefinition;

    public reactiveFormRecordId = '';

    public error = '';

    private provinceOptions: EntidadesDropdownOption[] = [];

    public constructor(
        public override router: Router,
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public entidadesApi: EntidadesApiService,
    ) {
        super(
            route,
            router,
            dashboard,
            entidadesApi.EntidadesPUT,
            entidadesApi.EntidadesPOST,
            'entidades',
            'Editar entidad',
            'Crear entidad',
            'Duplicar entidad',
        );
    }// constructor();

    /**
     * Initializes the shared form definition and starts progressive dropdown loading.
     *
     * @public
     *
     * @returns Nothing.
     */
    public ngOnInit(): void {
        this.resetExternalDropdowns();
        const initialModel = this.getInitialModel();

        this.reactiveFormRecordId = this.resolveReactiveFormRecordId(initialModel);
        this.form = new EntidadesFormDefinition(this.getType(), this.initializeHostModel(initialModel as EntidadModel));
        this.updateHostModel(this.form.model as EntidadModel);

        void this.initFormData();
        this.softInit(this.getType());

    }// ngOnInit();

    /**
     * Synchronizes stable reactive form changes with the shared form definition.
     *
     * @public
     *
     * @param event - Stable form value change emitted by the reactive builder.
     *
     * @returns Nothing.
     */
    public stableValueChanges(event: ReactiveFormStableValueChange<EntidadModel>): void {
        const stableValue = this.captureStableValueChange(event.value as EntidadModel);

        this.form.model = { ...this.form.model, ...stableValue };
        this.form.lastChangedFieldName = event.changedField;
        this.updateHostModel(this.form.model as EntidadModel);

        if (event.changedField === 'pais') {
            this.syncProvinceOptions();
        }// if();
    }// stableValueChanges();


    /**
     * Resets pending requests and returns to the entity list.
     *
     * @public
     *
     * @returns Nothing.
     */
    public override goBack(): void {
        this.request.reset();
        this.router.navigate(['dashboard', 'reto-entidades']);
    }// goBack();

    /**
     * Returns the initial model used before the backend record is loaded.
     *
     * @private
     *
     * @returns Initial entity model values.
     */
    private getInitialModel(): Partial<EntidadModel> {
        if (this.getType() === FormType.CREATION) {
            return { pais: EntidadesFormComponent.SPAIN_COUNTRY_CODE };
        }// if();

        return this.getModel() as Partial<EntidadModel>;
    }// getInitialModel();

    /**
     * Loads dropdown data and notifies the reactive form builder when ready.
     *
     * @private
     *
     * @returns A promise resolved when dropdown initialization finishes.
     */
    private async initFormData(): Promise<void> {
        try {
            this.provinceOptions = this.buildProvinceOptions(await this.entidadesApi.provinciasGET.toPromise());
            this.syncProvinceOptions();
        } catch (error) {
            this.error = 'No se han podido cargar los datos auxiliares de entidades.';
            console.error(this.error, error);
        } finally {
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
        }
    }// initFormData();

    /**
     * Converts API provinces into dropdown options.
     *
     * @private
     *
     * @param provinces - Province records returned by the API.
     *
     * @returns Dropdown options used by the reactive form builder.
     */
    private buildProvinceOptions(provinces: ProvinciasInterface[]): EntidadesDropdownOption[] {
        return [
            { label: '...', value: null },
            ...provinces.map((province) => ({
                label: province.nombre ?? '',
                value: province.id?.toString() ?? null,
            })),
        ];
    }// buildProvinceOptions();

    /**
     * Shows Spanish provinces when Spain is selected or while edit data is still loading.
     *
     * @private
     *
     * @returns Nothing.
     */
    private syncProvinceOptions(): void {
        const selectedCountry = String(this.form.model.pais ?? '');

        if (selectedCountry === '' || selectedCountry === EntidadesFormComponent.SPAIN_COUNTRY_CODE) {
            this.form.setProvinceOptions(this.provinceOptions);
        } else {
            this.form.resetProvinceOptions();
        }// if();

        this.refreshReactiveDropdownMappings();
    }// syncProvinceOptions();

    private refreshReactiveDropdownMappings(): void {
        ReactiveDropdownSyncController.refreshMappings({ formBuilder: this.formBuilderRef });
    }// refreshReactiveDropdownMappings();
}// class;

